import { type ReactNode } from 'react';
import clsx from 'clsx';
import { TransparentButton } from '../TransparentButton/TransparentButton';
import { ListItemMain, type ListItemMainProps } from './ListItemMain';
import styles from './ListItem.module.css';

interface BaseProps extends ListItemMainProps {
  leading: ReactNode;
  action?: ReactNode;
  alignment: 'top' | 'center';
  onClick?: () => void;
  ariaLabel?: string;
  noDivider?: boolean;
  className?: string;
}

interface ClickableProps extends BaseProps {
  onClick: () => void;
  ariaLabel: string;
}

interface ReadOnlyProps extends BaseProps {
  onClick?: never;
  ariaLabel?: never;
}

export function ListItem({
  alignment,
  variant,
  title,
  supporting,
  action,
  leading,
  onClick,
  ariaLabel,
  noDivider = false,
  className,
}: ClickableProps | ReadOnlyProps) {
  const classNames = alignment === 'top' ? [styles.top] : [styles.center];
  if (noDivider === true) {
    classNames.push(styles['no-divider']);
  }

  const body = (
    <>
      {leading}
      <div className={styles.main}>
        <ListItemMain variant={variant} title={title} supporting={supporting} />
      </div>
      {action}
    </>
  );

  if (onClick === undefined) {
    return (
      <li className={[...classNames, styles.inert, className].join(' ')}>
        {body}
      </li>
    );
  }

  return (
    <li className={`${styles.li} ${className}`}>
      <TransparentButton
        className={[styles.button, ...classNames].join(' ')}
        ariaLabel={ariaLabel}
        onClick={onClick}
      >
        {body}
      </TransparentButton>
    </li>
  );
}

type ListItemImageBaseProps = {
  variant: 'small' | 'medium' | 'large' | 'illustration' | 'icon';
  src?: string | null;
  height?: number;
  width?: number;
  icon?: ReactNode;
  ariaLabel?: string;
};

interface ImageProps extends ListItemImageBaseProps {
  variant: 'small' | 'medium' | 'large';
  src?: string | null;
  height?: number;
  width?: number;
  icon?: never;
  fallbackImage?: ReactNode;
}

interface IllustrationProps extends ListItemImageBaseProps {
  variant: 'illustration';
  icon: ReactNode;
  src?: never;
  height?: never;
  width?: never;
}

interface IconProps extends ListItemImageBaseProps {
  variant: 'icon';
  icon: ReactNode;
  src?: never;
  height?: never;
  width?: never;
}

function getImageClassName(variant: ImageProps['variant']) {
  switch (variant) {
    case 'large': {
      return styles['image-large'];
    }
    case 'medium': {
      return styles['image-medium'];
    }
    case 'small': {
      return styles['image-small'];
    }
  }
}

ListItem.Image = function (props: ImageProps | IllustrationProps | IconProps) {
  const { variant } = props;

  switch (variant) {
    case 'icon':
    case 'illustration': {
      const { icon } = props;
      const className =
        variant === 'illustration' ? styles.illustration : styles.icon;

      return <span className={className}>{icon}</span>;
    }

    case 'small':
    case 'medium':
    case 'large': {
      const { src, height, width, fallbackImage } = props;
      const className = getImageClassName(variant);

      if (src) {
        return (
          <div className={className}>
            <img
              loading="lazy"
              src={src}
              height={height}
              width={width}
              className={styles.image}
              alt=""
            />
          </div>
        );
      }
      return (
        <div className={clsx({ [className]: true, [styles.image]: true })}>
          {fallbackImage}
        </div>
      );
    }
  }
};
